import React from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Check, Eye, Loader2, LogOut, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { RichHtml } from '@/components/ui/rich-html'
import { MemberFieldsForm } from '@/components/features/members/MemberFieldsForm'
import { apiValues, mergeFields, missingRequired, valuesOf } from '@/components/features/members/memberFields'
import { videoEmbedUrl } from '@/components/features/courses/courseUtils'
import { useCommunity } from '@/lib/CommunityContext'
import { sdk } from '@/lib/sdk'
import { cn } from '@/lib/utils'
import { useCommunityLogo } from '@/lib/useDarkTheme'
import type { MemberFieldValue } from '@/lib/sdk-official/resources/member-import'
import type { WelcomeMemberScreen, WelcomeMode, WelcomeVideo } from '@/types/welcome'

/** What finishing a screen sends (`POST /api/welcome/progress`, without the step). */
export interface WelcomeStepAnswer {
  rulesAccepted?: boolean
  videoFinished?: boolean
  fields?: Record<string, unknown>
}

interface WelcomeFlowProps {
  screens: WelcomeMemberScreen[]
  step: number
  mode: WelcomeMode
  /** Finishes the current screen; rejects with the API's error (422 per field: `fields.<key>`). */
  onSubmit: (answer: WelcomeStepAnswer) => Promise<void>
  onSkip?: () => Promise<void>
  /** "Ver como membro": nothing is recorded, and the bar says so. */
  preview?: boolean
  onClose?: () => void
  onSignOut?: () => void
}

/** The uploaded video: its signed link, played natively so the end can be told ("Continuar só depois do vídeo"). */
const UploadedVideo = ({ mediaId, onEnded }: { mediaId: string; onEnded: () => void }) => {
  const { t } = useTranslation('welcome')
  const [src, setSrc] = React.useState<{ url: string; poster?: string } | null>(null)
  const [failed, setFailed] = React.useState(false)
  React.useEffect(() => {
    let alive = true
    setSrc(null); setFailed(false)
    sdk.media.getUrl(mediaId)
      .then((r) => { if (alive) { if (r.url) setSrc({ url: r.url, poster: r.posterUrl || r.thumbnailUrl }); else setFailed(true) } })
      .catch(() => { if (alive) setFailed(true) })
    return () => { alive = false }
  }, [mediaId])
  if (failed) return <p className="rounded-lg bg-muted p-4 text-sm text-foreground-secondary" data-welcome-video-failed>{t('flow.videoFailed')}</p>
  if (!src) return <div className="aspect-video w-full animate-pulse rounded-xl bg-muted" aria-hidden />
  return (
    <video src={src.url} poster={src.poster} controls playsInline preload="metadata" onEnded={onEnded}
      className="aspect-video w-full rounded-xl bg-black" data-welcome-video="upload" />
  )
}

const ScreenVideo = ({ video, onEnded }: { video: WelcomeVideo; onEnded: () => void }) => {
  const { t } = useTranslation('welcome')
  if (video.mediaId) return <UploadedVideo mediaId={video.mediaId} onEnded={onEnded} />
  const embed = videoEmbedUrl(video.url)
  if (!embed) return null
  return (
    <div className="aspect-video w-full overflow-hidden rounded-xl bg-black">
      <iframe src={embed} title={t('flow.videoTitle')} className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture; fullscreen" allowFullScreen data-welcome-video="link" />
    </div>
  )
}

/**
 * F-155: the welcome screens, one at a time, before the community. Each screen
 * has a title, formatted text and, optionally, a video, the rules to accept
 * and profile fields. "Continuar" finishes the screen (the API records it, so
 * leaving and coming back resumes there); the last one is "Entrar na
 * comunidade". Skippable welcomes show "Pular". The same component draws the
 * owner's "Ver como membro", where nothing is recorded.
 */
export const WelcomeFlow = ({ screens, step, mode, onSubmit, onSkip, preview = false, onClose, onSignOut }: WelcomeFlowProps) => {
  const { t } = useTranslation(['welcome', 'members'])
  const { community } = useCommunity()
  const logo = useCommunityLogo()
  const index = Math.min(Math.max(step, 0), Math.max(screens.length - 1, 0))
  const screen = screens[index]
  const last = index === screens.length - 1

  const fields = React.useMemo(() => mergeFields(null, (screen?.fields ?? []).map((f): MemberFieldValue => ({
    key: f.key, label: f.label, type: f.type, value: (f.value as MemberFieldValue['value']) ?? null,
    options: f.options ?? null, minSelections: f.minSelections ?? null, maxSelections: f.maxSelections ?? null, required: f.required
  }))), [screen])
  const [values, setValues] = React.useState<Record<string, string>>({})
  const [accepted, setAccepted] = React.useState(false)
  const [videoDone, setVideoDone] = React.useState(false)
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [error, setError] = React.useState('')
  const [busy, setBusy] = React.useState<'next' | 'skip' | null>(null)
  const top = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    setValues(valuesOf(fields)); setAccepted(false); setVideoDone(false); setErrors({}); setError('')
    top.current?.scrollIntoView({ block: 'start' })
  }, [index, fields])

  if (!screen) return null
  const mustWatch = Boolean(screen.video?.mediaId && screen.video.requireFinish)
  const blockedBy = screen.rules && !accepted ? 'rules' : mustWatch && !videoDone ? 'video' : null

  const next = async () => {
    if (busy || blockedBy) return
    const missing = missingRequired(fields, values, (label) => t('members:fieldsSection.requiredError', { label }))
    setErrors(missing)
    if (Object.keys(missing).length) return
    setBusy('next'); setError('')
    try {
      await onSubmit({
        ...(screen.rules ? { rulesAccepted: true } : {}),
        ...(mustWatch ? { videoFinished: true } : {}),
        ...(fields.length ? { fields: apiValues(fields, values) } : {})
      })
    } catch (e) {
      const data = (e as { response?: { data?: { message?: string; errors?: Array<{ param?: string; message?: string }> } } })?.response?.data
      const byField: Record<string, string> = {}
      for (const item of data?.errors ?? []) if (item.param?.startsWith('fields.') && item.message) byField[item.param.slice('fields.'.length)] = item.message
      setErrors(byField)
      setError(Object.keys(byField).length ? '' : data?.message || t('welcome:flow.failed'))
    } finally { setBusy(null) }
  }
  const skip = async () => {
    if (!onSkip || busy) return
    setBusy('skip'); setError('')
    try { await onSkip() } catch (e) {
      setError((e as { response?: { data?: { message?: string } } })?.response?.data?.message || t('welcome:flow.failed'))
    } finally { setBusy(null) }
  }

  /* On the body, at z-50: inside a page ("Ver como membro") a transformed ancestor would pin it to that box, and above
     z-50 the dropdowns of a choice field (Radix, z-50, opened later on the body) would open behind it. */
  return createPortal((
    <div className="fixed inset-0 z-50 overflow-y-auto bg-surface-muted" role="dialog" aria-modal="true" aria-labelledby="welcome-title" data-testid="welcome-flow" data-preview={preview}>
      <div ref={top} />
      {preview && (
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 bg-surface-inverse px-4 py-2 text-sm text-surface" data-welcome-preview-bar>
          <span className="flex items-center gap-2"><Eye className="h-4 w-4 shrink-0" />{t('welcome:flow.previewBar')}</span>
          <button type="button" onClick={onClose} className="flex items-center gap-1 rounded px-2 py-1 font-medium hover:bg-surface/10" data-welcome-preview-close>
            <X className="h-4 w-4" />{t('welcome:flow.previewClose')}
          </button>
        </div>
      )}
      <header className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-4 pt-6 sm:px-6">
        <div className="flex min-w-0 items-center gap-2">
          {logo.url && <img src={logo.url} alt="" className={cn('h-8 w-8 shrink-0 rounded-lg object-cover', logo.plate && 'theme-light bg-surface p-0.5 object-contain')} />}
          <span className="truncate font-semibold text-foreground">{community?.name}</span>
        </div>
        {!preview && onSignOut && (
          <button type="button" onClick={onSignOut} className="flex shrink-0 items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-welcome-signout>
            <LogOut className="h-4 w-4" />{t('welcome:flow.signOut')}
          </button>
        )}
      </header>

      <main className="mx-auto max-w-2xl px-4 py-6 sm:px-6 sm:py-8">
        {screens.length > 1 && (
          <div className="mb-4" data-welcome-steps>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('welcome:flow.step', { current: index + 1, total: screens.length })}</p>
            <div className="flex gap-1.5" aria-hidden>
              {screens.map((s, i) => <span key={s.id ?? i} className={cn('h-1.5 flex-1 rounded-full', i <= index ? 'bg-primary' : 'bg-muted')} />)}
            </div>
          </div>
        )}
        <article className="space-y-5 rounded-2xl bg-surface p-5 shadow-sm sm:p-8">
          <h1 id="welcome-title" className="text-balance text-2xl font-bold text-foreground sm:text-3xl">{screen.title}</h1>
          {screen.body && <RichHtml html={screen.body} className="prose prose-sm max-w-none text-foreground-secondary sm:prose-base" />}
          {screen.video && <ScreenVideo video={screen.video} onEnded={() => setVideoDone(true)} />}
          {mustWatch && !videoDone && <p className="text-xs text-muted-foreground" data-welcome-must-watch>{t('welcome:flow.mustWatch')}</p>}

          {screen.rules && (
            <section className="space-y-3 rounded-xl border border-border bg-surface-muted p-4" data-welcome-rules>
              <h2 className="text-sm font-semibold text-foreground">{t('welcome:flow.rulesTitle')}</h2>
              <p className="max-h-64 overflow-y-auto whitespace-pre-wrap text-sm text-foreground-secondary">{screen.rules.text}</p>
              <label className="flex cursor-pointer items-start gap-2 text-sm font-medium text-foreground">
                <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-border accent-[var(--color-primary-600,currentColor)]" data-welcome-accept />
                {t('welcome:flow.accept')}
              </label>
            </section>
          )}

          {fields.length > 0 && (
            <MemberFieldsForm fields={fields} values={values} onChange={setValues} disabled={Boolean(busy)} errors={errors} className="sm:grid-cols-1" />
          )}

          {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}

          <div className="flex flex-col-reverse gap-2 border-t border-muted pt-5 sm:flex-row sm:items-center sm:justify-between">
            {mode === 'SKIPPABLE' && onSkip ? (
              <Button type="button" variant="ghost" className="w-full sm:w-auto" disabled={Boolean(busy)} onClick={() => void skip()} data-welcome-skip>
                {busy === 'skip' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t('welcome:flow.skip')}
              </Button>
            ) : <span className="hidden sm:block" />}
            <Button type="button" variant="primary" size="lg" className="w-full sm:w-auto" disabled={Boolean(busy) || Boolean(blockedBy)} onClick={() => void next()} data-welcome-next>
              {busy === 'next' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : last ? <Check className="mr-2 h-4 w-4" /> : null}
              {last ? t('welcome:flow.finish') : t('welcome:flow.next')}
              {!last && busy !== 'next' && <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>
          </div>
          {/* The video's own line (under it) already says why "Continuar" waits. */}
          {blockedBy === 'rules' && <p className="text-right text-xs text-muted-foreground" data-welcome-blocked="rules">{t('welcome:flow.needAccept')}</p>}
        </article>
      </main>
    </div>
  ), document.body)
}
